import { Amplify, type ResourcesConfig } from 'aws-amplify'
import {
    confirmResetPassword,
    confirmSignIn,
    fetchAuthSession,
    resetPassword,
    signIn,
    signOut,
    type AuthSession,
    type ConfirmResetPasswordInput,
    type ConfirmSignInInput,
    type ConfirmSignInOutput,
    type ResetPasswordInput,
    type ResetPasswordOutput,
    type SignInInput,
    type SignInOutput,
} from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { sessionStorage, type KeyValueStorageInterface } from 'aws-amplify/utils'
import type {
    AuthClient,
    InAppCognitoConfig,
    NoPendingSignIn,
    SignInResult,
    Tokens,
} from './authClient'

export type AmplifyLibrary = {
    configure(config: ResourcesConfig): void
    setKeyValueStorage(storage: KeyValueStorageInterface): void
    fetchAuthSession(): Promise<AuthSession>
    signIn(input: SignInInput): Promise<SignInOutput>
    confirmSignIn(input: ConfirmSignInInput): Promise<ConfirmSignInOutput>
    resetPassword(input: ResetPasswordInput): Promise<ResetPasswordOutput>
    confirmResetPassword(input: ConfirmResetPasswordInput): Promise<void>
    signOut(): Promise<void>
}

const amplify: AmplifyLibrary = {
    configure: (config) => Amplify.configure(config),
    setKeyValueStorage: (storage) => cognitoUserPoolsTokenProvider.setKeyValueStorage(storage),
    fetchAuthSession,
    signIn,
    confirmSignIn,
    resetPassword,
    confirmResetPassword,
    signOut,
}

type Challenge = 'mfa' | 'newPassword'

const noSession: AuthSession = {}

const passwordResetRequired = 'Password reset required for the user'

const noSessionAfterSignIn = 'Signed in, but no session was stored'

const messageOf = (error: unknown) => (error instanceof Error ? error.message : String(error))

const failedWith = (error: unknown): SignInResult => ({ kind: 'failed', message: messageOf(error) })

const isAlreadySignedIn = (error: unknown) => {
    return error instanceof Error && error.name === 'UserAlreadyAuthenticatedException'
}

const unsupported = (signInStep: string): SignInResult => ({
    kind: 'failed',
    message: `Unsupported sign-in step: ${signInStep}`,
})

export const createAmplifyAuthClient = (
    config: InAppCognitoConfig,
    library: AmplifyLibrary = amplify,
): AuthClient => {
    library.configure({
        Auth: { Cognito: { userPoolId: config.userPoolId, userPoolClientId: config.client_id } },
    })

    library.setKeyValueStorage(sessionStorage)

    let pendingChallenge: Challenge | undefined

    const currentSession = async (): Promise<Tokens | undefined> => {
        const { tokens } = await library.fetchAuthSession().catch(() => noSession)
        if (!tokens?.idToken) return undefined

        return {
            accessToken: tokens.accessToken.toString(),
            idToken: tokens.idToken.toString(),
        }
    }

    const signedInWithCurrentSession = async (): Promise<SignInResult> => {
        const tokens = await currentSession()
        if (!tokens) return { kind: 'failed', message: noSessionAfterSignIn }

        return { kind: 'signedIn', tokens }
    }

    const signInResultFrom = async ({ nextStep }: SignInOutput): Promise<SignInResult> => {
        switch (nextStep.signInStep) {
            case 'DONE':
                return signedInWithCurrentSession()

            case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
                pendingChallenge = 'mfa'

                return {
                    kind: 'mfaRequired',
                    medium: nextStep.codeDeliveryDetails?.deliveryMedium ?? 'SMS',
                    destination: nextStep.codeDeliveryDetails?.destination ?? '',
                }

            case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
                pendingChallenge = 'newPassword'

                return {
                    kind: 'newPasswordRequired',
                    requiredAttributes: nextStep.missingAttributes ?? [],
                }

            // Today: reported as failed, with Cognito's own message for this case.
            case 'RESET_PASSWORD':
                return { kind: 'failed', message: passwordResetRequired }

            default:
                return unsupported(nextStep.signInStep)
        }
    }

    const challengeResultFrom = async ({
        nextStep,
    }: ConfirmSignInOutput): Promise<SignInResult> => {
        // Today: a further challenge (e.g. SMS MFA after a new password) fails the sign-in.
        if (nextStep.signInStep !== 'DONE') return unsupported(nextStep.signInStep)

        pendingChallenge = undefined
        return signedInWithCurrentSession()
    }

    // Amplify refuses to sign in over a stored session; today's client signs in again.
    const signInReplacingStoredSession = (input: SignInInput) => {
        return library.signIn(input).catch(async (error) => {
            if (!isAlreadySignedIn(error)) throw error

            await library.signOut()

            return library.signIn(input)
        })
    }

    const continueSignIn = async (
        challenge: Challenge,
        challengeResponse: string,
    ): Promise<SignInResult | NoPendingSignIn> => {
        if (pendingChallenge !== challenge) return { kind: 'noPendingSignIn' }

        return library.confirmSignIn({ challengeResponse }).then(challengeResultFrom, failedWith)
    }

    return {
        currentSession,
        signIn: (username, password) => {
            pendingChallenge = undefined

            return signInReplacingStoredSession({ username, password }).then(
                signInResultFrom,
                failedWith,
            )
        },
        confirmMfa: (code) => continueSignIn('mfa', code),
        completeNewPassword: (password) => continueSignIn('newPassword', password),
        requestPasswordReset: async (username) => {
            await library.resetPassword({ username })
        },
        confirmPasswordReset: async (username, code, password) => {
            await library.confirmResetPassword({
                username,
                confirmationCode: code,
                newPassword: password,
            })
        },
        signOut: () => {
            // Amplify's signOut keeps a sign-in that is waiting for a challenge answer.
            pendingChallenge = undefined
            return library.signOut()
        },
    }
}
